/**
 * Nationality-specific config for the "Spain visa from USA" guide pages.
 *
 * Add a new entry (keyed by nationality slug) to generate a new page at
 * /spain-visa-from-usa-for-{slug}. Every other slug on this route will 404
 * from the page component itself.
 *
 * All *shared* content (BLS process, documents, photo specs, processing
 * time, etc.) lives in `spainUsaGuideShared.js` — this file is strictly the
 * per-nationality overlay.
 */

export const spainUsaNationalityConfig = {
  indians: {
    slug: "indians",
    nationality: "Indian",
    nationality_plural: "Indians",
    // ISO-2 country code — used as the safest join key when matching a
    // selector's nationality choice against config entries.
    country_code: "IN",
    passport: "Indian passport",
    // Standard fee band. Indians are NOT in the reduced-fee group.
    fee_band: {
      band_label: "Standard band",
      rows: [
        { label: "Adult / Minor age 12 and above", amount: "USD 106" },
        { label: "Child age 6–11", amount: "USD 53" },
        { label: "Child age 5 and below", amount: "Free" },
      ],
    },
    origin_note:
      "Indian passport holders who legally reside in the United States must apply for the Spain Schengen visa through BLS International in the US — not through the Spanish consulate in India. This applies to residents on a Green Card, H1B, H4, L1, L2, O1, F1 (with a valid I-20 and, when applicable, an approved advance parole document), OPT-EAD (with advance parole for re-entry), refugee travel document holders, and other valid US residence categories. What matters is that you hold a US residence document that permits you to return to the US after your Schengen trip; if it does not, the Spanish consulate in the US will not accept your file and you must apply from India.",
    faqs: [
      {
        q: "Can Indian citizens apply for a Spain Schengen visa from the USA?",
        a: "Yes. Indian passport holders who legally reside in the United States apply for the Spain Schengen visa through BLS International in the US — not in India. Your file is filed at the BLS center covering your state of residence, and processed by the Spanish consulate with jurisdiction over that BLS center. You must hold a US residence document (Green Card, H1B, L1, F1 with I-20, OPT-EAD with advance parole, etc.) that allows you to re-enter the US after your trip.",
      },
      {
        q: "Can I apply for a Spain Schengen visa on an H1B, F1, or Green Card?",
        a: "Yes — all three are accepted, provided your status supports re-entry to the US at the time of return. Green Card holders present the physical card. H1B / L1 / O1 applicants present the valid visa stamp in their passport plus a recent I-797. F1 students must show a valid I-20 signed for travel; if you are on OPT, you need the EAD card and — for re-entry after most Schengen trips — an approved advance parole document is safer. The consulate reserves the right to refuse a file if it doubts the applicant's ability to return to the US.",
      },
      {
        q: "Do Indian passport holders have to apply in India instead?",
        a: "Only if you don't have valid US residence that permits re-entry. If you are a tourist or short-term visitor in the US (B1/B2, ESTA, F1 without OPT, etc.) with no US residence, the Spanish consulate in the US will not treat you as a resident and you must apply from India. Being physically present in the US is not enough — the consulate requires proof that you live in the US and can return here after Schengen.",
      },
      {
        q: "What proof of US residence do Indian applicants need to submit?",
        a: "One primary document proving your legal US residence with re-entry rights (Green Card, valid US residence visa stamp such as H1B/L1/O1/E, an EAD paired with an approved advance parole for OPT/pending AOS, or a refugee travel document) plus proof of jurisdiction (a recent utility bill, lease, mortgage statement, university enrollment with transcript, or health insurance bill in your name at your US address). US driver's licenses and state IDs are not accepted as jurisdiction proof by BLS.",
      },
    ],
  },
};

/** Return the config entry for a slug, or null if unknown. */
export function getNationalityConfig(slug) {
  if (!slug) return null;
  return spainUsaNationalityConfig[slug.toLowerCase()] || null;
}

/**
 * Return the pathname of a dedicated nationality landing page for the given
 * (nationality, residence, destination) combination — or null if no
 * dedicated page exists.
 *
 * All arguments are ISO-2 country codes matching what the guide selectors
 * emit (e.g. "IN", "US", "ES"). Comparison is case-insensitive.
 *
 * Adding a new dedicated page = adding a new entry to
 * `spainUsaNationalityConfig` with the right `country_code`. Zero extra
 * routing code.
 */
export function findDedicatedRoute({ nationalityCode, residenceCode, destinationCode }) {
  if (!nationalityCode || !residenceCode || !destinationCode) return null;
  const nat = nationalityCode.trim().toUpperCase();
  const res = residenceCode.trim().toUpperCase();
  const dst = destinationCode.trim().toUpperCase();
  // Every entry in spainUsaNationalityConfig is a (nationality + USA + Spain)
  // combination — that's the whole point of this config. Match on nationality.
  if (res !== "US" || dst !== "ES") return null;
  for (const entry of Object.values(spainUsaNationalityConfig)) {
    if ((entry.country_code || "").toUpperCase() === nat) {
      return `/spain-visa-from-usa-for-${entry.slug}`;
    }
  }
  return null;
}
